import React, { useState, useEffect } from 'react';
import {
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  Divider,
  Chip,
  TextField,
  IconButton,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  AttachFile as AttachFileIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Build as BuildIcon,
  Factory as FactoryIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import BootstrapDialog, {
  BootstrapDialogTitle,
} from '../../../components/common/dialog/BootstrapDialog';
import EmpSelectbox from '../../../components/common/form/autocomplete/EmpSelectbox';
import OutsourceDialog from './OutsourceDialog';
import * as manufacturingApi from '../../../lib/api/manufacturing';
import * as fileApi from '../../../lib/api/file';
import { toDateOnly } from '../../../utils/manufacturing/routingSync';
import { useFormik } from 'formik';

const STATUS_CONFIG = {
  PENDING: { label: '대기', color: '#9e9e9e', bgcolor: '#f5f5f5' },
  IN_PROGRESS: { label: '진행중', color: '#ff9800', bgcolor: '#fff3e0' },
  COMPLETED: { label: '완료', color: '#4caf50', bgcolor: '#e8f5e9' },
  SKIPPED: { label: '건너뜀', color: '#9e9e9e', bgcolor: '#f5f5f5' },
};

/**
 * 공정 상세 다이얼로그
 * - 공정 라우팅 기반 상세 정보 표시
 * - 담당자 변경, 종료예정일 변경 가능
 * - 첨부파일 확인
 */
const CLOSED_STATUSES = ['COMPLETED', 'CANCELLED', 'SKIPPED'];

const ProcessDetailDialog = ({
  open,
  onClose,
  processData,
  planData,
  onUpdate,
  canUpdatePhaseAssignment = false,
  canUpdateClosedPhaseAssignment = false,
}) => {
  const [isEditingWorker, setIsEditingWorker] = useState(false);
  const [isEditingEndDate, setIsEditingEndDate] = useState(false);
  const [endDate, setEndDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState([]);
  const [fileLoading, setFileLoading] = useState(false);
  const [openOutsourceDialog, setOpenOutsourceDialog] = useState(false);

  const formik = useFormik({
    initialValues: {
      workerUuid: '',
    },
    onSubmit: () => {},
  });

  useEffect(() => {
    if (processData) {
      formik.setFieldValue('workerUuid', processData.workerUuid || '');
      // planEndDt/actualEndDt 는 매퍼가 datetime 문자열("yyyy-MM-dd HH:mm:ss")로 내려준다.
      // HTML date input(type="date")은 정확히 yyyy-MM-dd 형식만 받으므로 날짜만 잘라 넣는다.
      setEndDate(toDateOnly(processData.planEndDt) || toDateOnly(processData.actualEndDt));
      setIsEditingWorker(false);
      setIsEditingEndDate(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processData]);

  // 파일 목록 로드
  useEffect(() => {
    const fetchFiles = async () => {
      if (!open || !processData?.routingDetUuid) return;
      setFileLoading(true);
      try {
        const response = await fileApi.getProcessFiles(processData.routingDetUuid);
        if (response.data && response.data.list) {
          setFiles(response.data.list.map(f => ({
            id: f.fileUuid,
            fileUuid: f.fileUuid,
            fileName: f.fileNm,
            fileSize: f.fileSize ? `${(f.fileSize / 1024).toFixed(1)}KB` : '-',
            uploadDate: f.crtDt ? f.crtDt.split(' ')[0] : '-',
          })));
        } else {
          setFiles([]);
        }
      } catch (err) {
        console.error('공정 파일 조회 에러:', err);
        setFiles([]);
      } finally {
        setFileLoading(false);
      }
    };
    fetchFiles();
  }, [open, processData?.routingDetUuid]);

  const handleSave = async () => {
    if (!processData?.routingDetUuid) return;
    setSaving(true);
    try {
      const updateData = {};
      let changed = false;

      // 담당자 변경
      if (isEditingWorker && formik.values.workerUuid && formik.values.workerUuid !== processData.workerUuid) {
        if (!canEditWorker) {
          alert('공정 담당자 변경 권한이 없습니다.');
          return;
        }
        updateData.workerUuid = formik.values.workerUuid;
        changed = true;
      }

      // 종료예정일 변경
      // endDate 는 date input 값이라 'yyyy-MM-dd', processData.planEndDt 는 datetime 문자열이라
      // 형식이 달라 그냥 비교하면 항상 다르다고 나온다(편집만 열고 아무것도 안 바꿔도 changed=true).
      // 비교 대상도 같은 형식으로 맞춘다.
      if (isEditingEndDate && endDate !== toDateOnly(processData.planEndDt)) {
        if (!isEditable) {
          alert('완료/취소된 공정은 종료 예정일을 변경할 수 없습니다.');
          return;
        }
        updateData.planEndDt = endDate;
        changed = true;
      }

      if (changed) {
        await manufacturingApi.updateRoutingDetail(processData.routingDetUuid, updateData);
        if (onUpdate) onUpdate();
      }
      onClose();
    } catch (err) {
      console.error('공정 정보 수정 에러:', err);
      alert('수정에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleFileDownload = async (file) => {
    try {
      const response = await fileApi.downloadFile(file.fileUuid || file.id);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('파일 다운로드 에러:', err);
      alert('파일 다운로드에 실패했습니다.');
    }
  };

  if (!processData) return null;

  const statusConfig = STATUS_CONFIG[processData.processStatus] || STATUS_CONFIG.PENDING;
  const isCompleted = processData.processStatus === 'COMPLETED' || processData.processStatus === 'SKIPPED';
  const isClosed = CLOSED_STATUSES.includes(processData.processStatus);
  const isEditable = !isClosed; // 진행전/진행중만 일정 수정 가능
  const canEditWorker = isClosed ? canUpdateClosedPhaseAssignment : canUpdatePhaseAssignment;
  const canShowSave = canEditWorker || isEditable;
  const canSave =
    (isEditingWorker && canEditWorker) ||
    (isEditingEndDate && isEditable);

  return (
    <BootstrapDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <BootstrapDialogTitle onClose={onClose}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BuildIcon color="primary" />
            <Typography variant="h6">공정 상세</Typography>
          </Box>
          <Chip
            label={statusConfig.label}
            sx={{ backgroundColor: statusConfig.color, color: 'white', fontWeight: 'bold' }}
          />
        </Box>
      </BootstrapDialogTitle>

      <DialogContent dividers>
        <Box sx={{ py: 1 }}>
          {/* 공정 기본 정보 */}
          <Box sx={{ bgcolor: statusConfig.bgcolor, p: 2, borderRadius: 2, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {processData.processSeq}. {processData.processNm}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {processData.stdWorkTime && (
                <Typography variant="body2" color="text.secondary">
                  표준시간: <strong>{processData.stdWorkTime}h</strong>
                </Typography>
              )}
              {processData.isRequired === 'Y' && (
                <Chip label="필수 공정" size="small" color="error" variant="outlined" />
              )}
            </Box>
          </Box>

          <Grid container spacing={2.5}>
            {/* 담당자 */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" fontWeight="bold">담당자</Typography>
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              {isEditingWorker && canEditWorker ? (
                <Box>
                  <EmpSelectbox
                    formik={formik}
                    name="workerUuid"
                    label="담당자 선택"
                    variant="outlined"
                    margin="none"
                  />
                  <Button size="small" onClick={() => setIsEditingWorker(false)} sx={{ mt: 0.5 }}>
                    취소
                  </Button>
                </Box>
              ) : (
                <TextField
                  fullWidth
                  value={processData.workerNm || planData?.productionManagerName || '-'}
                  InputProps={{
                    readOnly: true,
                    endAdornment: canEditWorker ? (
                      <IconButton onClick={() => setIsEditingWorker(true)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    ) : null,
                  }}
                  variant="filled"
                  size="small"
                />
              )}
            </Grid>

            {/* 일정 정보 */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ScheduleIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" fontWeight="bold">일정 정보</Typography>
              </Box>
              <Divider sx={{ mb: 1.5 }} />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="시작 예정일"
                value={processData.planStartDt || '-'}
                InputProps={{ readOnly: true }}
                variant="filled"
                size="small"
              />
            </Grid>
            <Grid item xs={6}>
              {isEditingEndDate && isEditable ? (
                <Box>
                  <TextField
                    fullWidth
                    label="종료 예정일"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    size="small"
                  />
                  <Button size="small" onClick={() => setIsEditingEndDate(false)} sx={{ mt: 0.5 }}>
                    취소
                  </Button>
                </Box>
              ) : (
                <TextField
                  fullWidth
                  label="종료 예정일"
                  value={processData.planEndDt || '-'}
                  InputProps={{
                    readOnly: true,
                    endAdornment: isEditable ? (
                      <IconButton onClick={() => setIsEditingEndDate(true)} size="small">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    ) : null,
                  }}
                  variant="filled"
                  size="small"
                />
              )}
            </Grid>

            {/* 실제 일자 (시작/완료된 경우) */}
            {(processData.actualStartDt || processData.actualEndDt) && (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="실제 시작일"
                    value={processData.actualStartDt || '-'}
                    InputProps={{ readOnly: true }}
                    variant="filled"
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="실제 완료일"
                    value={processData.actualEndDt || '-'}
                    InputProps={{ readOnly: true }}
                    variant="filled"
                    size="small"
                  />
                </Grid>
              </>
            )}

            {/* 외주 정보 섹션 */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FactoryIcon fontSize="small" color={processData.processType === 'EXTERNAL' ? 'primary' : 'action'} />
                  <Typography variant="subtitle2" fontWeight="bold">외주 정보</Typography>
                  {processData.processType === 'EXTERNAL' && (
                    <Chip label="외주" size="small" color="primary" variant="filled" sx={{ height: 18, fontSize: '0.7rem' }} />
                  )}
                </Box>
                {!isCompleted && (
                  <Button
                    size="small"
                    variant={processData.processType === 'EXTERNAL' ? 'outlined' : 'contained'}
                    color="primary"
                    startIcon={<FactoryIcon />}
                    onClick={() => setOpenOutsourceDialog(true)}
                    sx={{ fontSize: '0.75rem' }}
                  >
                    {processData.processType === 'EXTERNAL' ? '외주 수정' : '외주 설정'}
                  </Button>
                )}
              </Box>
              <Divider sx={{ mb: 1.5 }} />

              {processData.processType === 'EXTERNAL' && processData.outsourceUuid ? (
                <Box sx={{ bgcolor: '#e3f2fd', borderRadius: 1.5, p: 1.5 }}>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>외주업체</Typography>
                      <Typography variant="body2" fontWeight="bold">{processData.partnerNm || '-'}</Typography>
                    </Grid>
                    {(processData.contactNm || processData.contactTel) && (
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>담당자</Typography>
                        </Box>
                        <Typography variant="body2">
                          {processData.contactNm || ''}
                          {processData.contactNm && processData.contactTel && ' · '}
                          {processData.contactTel || ''}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>납기 예정일</Typography>
                      <Typography variant="body2" fontWeight={processData.dueDate ? 600 : 400}>
                        {processData.dueDate || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem' }}>외주 상태</Typography>
                      <Chip
                        label={processData.outsourceStatusNm || processData.outsourceStatus || '-'}
                        size="small"
                        color={
                          processData.outsourceStatus === 'COMPLETED' ? 'success' :
                          processData.outsourceStatus === 'ORDERED' ? 'primary' :
                          processData.outsourceStatus === 'CANCELLED' ? 'error' : 'default'
                        }
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.72rem' }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ) : processData.processType === 'EXTERNAL' ? (
                <Alert severity="warning" sx={{ py: 0.5 }}>외주 정보가 없습니다. 외주 설정을 진행해주세요.</Alert>
              ) : (
                <Typography variant="body2" color="text.secondary">내부 공정입니다.</Typography>
              )}
            </Grid>

            {/* 첨부파일 */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 1 }}>
                <AttachFileIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" fontWeight="bold">
                  첨부파일 ({files.length})
                </Typography>
                {fileLoading && <CircularProgress size={16} />}
              </Box>
              <Divider sx={{ mb: 1.5 }} />

              {files.length > 0 ? (
                <List dense>
                  {files.map((file) => (
                    <ListItem
                      key={file.id}
                      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 0.5, bgcolor: '#fafafa' }}
                      secondaryAction={
                        <IconButton edge="end" size="small" onClick={() => handleFileDownload(file)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <FileIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={file.fileName}
                        secondary={`${file.fileSize} | ${file.uploadDate}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  첨부된 파일이 없습니다.
                </Typography>
              )}
            </Grid>

            {/* 비고 (완료된 경우 remarks 표시) */}
            {processData.remarks && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="작업 내용"
                  value={processData.remarks}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                  size="small"
                  multiline
                  rows={2}
                />
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>닫기</Button>
        {canShowSave && (
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={saving || !canSave}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? '저장중...' : '저장'}
          </Button>
        )}
      </DialogActions>

      {/* 외주 설정 다이얼로그 */}
      <OutsourceDialog
        open={openOutsourceDialog}
        onClose={() => setOpenOutsourceDialog(false)}
        processData={processData}
        planId={processData?.planId || planData?.planId}
        outsourceData={
          processData?.processType === 'EXTERNAL' && processData?.outsourceUuid
            ? {
                outsourceUuid: processData.outsourceUuid,
                partnerUuid: processData.partnerUuid,
                partnerNm: processData.partnerNm,
                contactNm: processData.contactNm,
                contactTel: processData.contactTel,
                orderDate: processData.orderDate,
                dueDate: processData.dueDate,
                remarks: processData.remarks,
                status: processData.outsourceStatus,
              }
            : null
        }
        onSaved={() => {
          setOpenOutsourceDialog(false);
          if (onUpdate) onUpdate();
        }}
      />
    </BootstrapDialog>
  );
};

export default ProcessDetailDialog;
